// ═══════════════════════════════════════════════════════════════════════════
// ClientManager — la ficha del cliente
//
// Era solo un editor de catálogo: datos fiscales, CSF y precios. Para saber
// qué se le había vendido a un restaurante había que salir a Cuentas y
// expandir su tarjeta; la ficha del cliente no sabía nada de su historia.
//
// Ahora es la ficha COMPLETA: datos + su historial + su saldo, con las mismas
// acciones que en las otras dos pantallas. Cuentas no desaparece: "¿quién me
// debe?" cruza clientes y no la contesta ninguna ficha individual.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { PRICING_RULES } from "../dominio/precios.js";
import { SAT_FORMA_PAGO, SAT_METODO_PAGO, SAT_REGIMEN_FISCAL, SAT_USO_CFDI } from "../fiscal/catalogos-sat.js";
import { mapFormaPago, mapMetodoPago, mapUsoCfdi } from "../fiscal/cfdi.js";
import { parseCSF } from "../fiscal/csf.js";
import { parsearDireccion } from "../dominio/direcciones.js";
import { fmt } from "../pdf/comunes.js";
import { saldoPendiente, estadoCobranza, remisionCancelada } from "../dominio/cobranza.js";
import { esperaFolio } from "../datos/remisiones-pg.js";
import { estadoComision, COMISION } from "../dominio/estado-comision.js";
import { puedeMembresia } from "../auth/permisos.js";
import { SolicitarPrecio } from "./SolicitarPrecio.jsx";
import { PriceListPreview } from "./PriceListPreview.jsx";
import { useRemisionAcciones } from "./RemisionAcciones.jsx";
import { Card, Label, NumInput, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { generateId } from "./utiles.js";

export function ClientManager({ clients, setClients, products, invoices = [], setInvoices,
                                membresia, onAbrirEnEditor, onIrAConfig }) {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | client id
  // El parseo de la CSF es ASÍNCRONO (con OCR puede tardar >30 s). Este ref
  // guarda a quién se está editando AHORA, para descartar resultados que
  // lleguen después de cambiar de cliente. Sin esto, la CSF del cliente A se
  // aplicaba al formulario del cliente B — contaminación real de datos,
  // presente también en el monolito (línea 4213 de v0.9.16).
  const editingRef = useRef(editing);
  useEffect(() => { editingRef.current = editing; }, [editing]);
  const [form, setForm] = useState({});
  const [csfStatus, setCsfStatus] = useState(""); // "" | "parsing" | "success" | "error"
  const csfInputRef = useRef(null);
  const [priceListClient, setPriceListClient] = useState(null);
  const [historialDe, setHistorialDe] = useState(null);   // id del cliente abierto
  const [solicitando, setSolicitando] = useState(null);   // cliente al que se le pide precio

  // ── Qué del cliente es del vendedor y qué no ─────────────────────────────
  //
  // El operador: "De los clientes solo puede editar las direcciones o la CSF,
  // no los descuentos, o la lista de precios personalizada."
  //
  // La línea está donde tiene que estar: el vendedor mantiene al día lo que
  // sabe por trato diario —a dónde se entrega, a quién se le habla, qué dice
  // su constancia fiscal— y no toca lo que fija cuánto paga. Lo segundo no se
  // le esconde: lo VE, y puede pedir que cambie. Lo que no puede es moverlo.
  const puedeDescuento = puedeMembresia(membresia, "editar_descuento");
  const puedePrecios = puedeMembresia(membresia, "editar_precios");
  const puedeSolicitar = puedeMembresia(membresia, "solicitar_precio");

  // Las mismas acciones que en Historial y Cuentas. Si no hay con qué
  // escribir remisiones (setInvoices), la ficha se queda como estaba.
  const acciones = useRemisionAcciones({
    clients, products, invoices, setInvoices: setInvoices || (() => {}),
    membresia, onAbrirEnEditor, onIrAConfig });

  // Remisiones y saldo por cliente. Se calcula una vez, no por tarjeta.
  const porCliente = useMemo(() => {
    const mapa = new Map();
    for (const inv of invoices) {
      const id = inv.client?.id;
      if (!id) continue;
      if (!mapa.has(id)) mapa.set(id, []);
      mapa.get(id).push(inv);
    }
    for (const lista of mapa.values())
      lista.sort((a, b) => new Date(b.date) - new Date(a.date));
    return mapa;
  }, [invoices]);

  const resumenDe = useCallback((id) => {
    const lista = porCliente.get(id) || [];
    const vivas = lista.filter(i => !remisionCancelada(i));
    return {
      lista,
      cuantas: lista.length,
      total: vivas.reduce((t, i) => t + (i.total || 0), 0),
      saldo: vivas.reduce((t, i) => t + saldoPendiente(i), 0),
      ultima: lista[0]?.date,
    };
  }, [porCliente]);

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.city.toLowerCase().includes(search.toLowerCase()) ||
    c.state.toLowerCase().includes(search.toLowerCase()) ||
    (c.rfc || "").toLowerCase().includes(search.toLowerCase())
  );

  const emptyForm = () => ({ name: "", legalName: "", street: "", colonia: "", cp: "", city: "", state: "", phone: "", rfc: "", email: "", att: "",
    taxSystem: "", regimenes: [], domicilioFiscalZip: "", fiscalStreet: "", fiscalColonia: "", fiscalCity: "", fiscalState: "",
    pricingRule: "default", discount: 20, ivaRate: 8,
    paymentMethod: "03", paymentTiming: "PUE", cfdiUse: "G01", iepsDisplay: "incluido", customPrices: {} });

  const startNew = () => { setForm(emptyForm()); setEditing("new"); setCsfStatus(""); setCsfResult(null); setPegado(""); setPropuesta(null); };

  const startEdit = (c) => {
    // Migrate legacy text values to SAT codes on edit
    const migrated = { ...c };
    if (migrated.paymentMethod && !SAT_FORMA_PAGO.find(f => f.code === migrated.paymentMethod)) {
      migrated.paymentMethod = mapFormaPago(migrated.paymentMethod);
    }
    if (migrated.paymentTiming && !SAT_METODO_PAGO.find(m => m.code === migrated.paymentTiming)) {
      migrated.paymentTiming = mapMetodoPago(migrated.paymentTiming);
    }
    if (migrated.cfdiUse && !SAT_USO_CFDI.find(u => u.code === migrated.cfdiUse)) {
      migrated.cfdiUse = mapUsoCfdi(migrated.cfdiUse);
    }
    setForm(migrated);
    setEditing(c.id);
    setCsfStatus("");
    setCsfResult(null);   // el panel de la CSF anterior no debe verse en otro cliente
    setPegado(""); setPropuesta(null);
  };

  const save = () => {
    if (!form.name.trim()) return alert("El nombre es requerido");
    if (editing === "new") {
      setClients(prev => [...prev, { ...form, id: generateId() }]);
    } else {
      setClients(prev => prev.map(c => c.id === editing ? { ...form } : c));
    }
    setEditing(null);
  };

  const remove = (id) => {
    if (confirm("¿Eliminar este cliente?")) setClients(prev => prev.filter(c => c.id !== id));
  };

  const [csfResult, setCsfResult] = useState(null); // stores parse result for display
  // Pegado de dirección — lote 2.5, observación 1
  const [pegado, setPegado] = useState("");
  const [propuesta, setPropuesta] = useState(null);

  const handleCSFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objetivo = editing;          // a quién pertenece ESTA constancia
    setCsfStatus("parsing");
    setCsfResult(null);
    try {
      const data = await parseCSF(file, (msg) => setCsfStatus(msg));
      if (editingRef.current !== objetivo) {
        // Cambió de cliente mientras se procesaba: descartar, jamás aplicar.
        setCsfStatus("");
        setCsfResult(null);
        alert("⚠️ La constancia terminó de procesarse DESPUÉS de que cambiaras de cliente.\n\nNo se aplicó a nadie. Abre el cliente correcto y súbela otra vez.");
        return;
      }
      setCsfResult(data);
      const updates = {};
      if (data.rfc && data.rfc !== "XAXX010101000" && data.rfc !== "XEXX010101000") updates.rfc = data.rfc;
      if (data.legalName) { updates.legalName = data.legalName; if (!form.name) updates.name = data.legalName; }
      if (data.taxSystem) updates.taxSystem = data.taxSystem;
      // TODOS los que trae la CSF, no solo el primero. El lector ya los sacaba
      // —y avisaba «el contribuyente tiene 4 regímenes»— pero al guardar se
      // quedaba uno y los otros se perdían. Con la lista completa, la remisión
      // puede ofrecer a escoger entre los que el SAT de verdad le reconoce
      // (migración 0061).
      if (Array.isArray(data.regimenes) && data.regimenes.length) {
        updates.regimenes = data.regimenes;
      }
      if (data.domicilioFiscalZip) updates.domicilioFiscalZip = data.domicilioFiscalZip;
      if (data.street) updates.fiscalStreet = data.street;
      if (data.colonia) updates.fiscalColonia = data.colonia;
      if (data.city) updates.fiscalCity = data.city;
      if (data.state) updates.fiscalState = data.state;
      if (data.curp) updates.curp = data.curp;
      // El domicilio de ENTREGA por omisión es el FISCAL: se copia solo si el
      // de entrega está vacío, y queda editable. No es derivación silenciosa —
      // un cliente con entrega distinta no debe depender de que nadie borre.
      if (Object.keys(updates).length > 0) {
        setForm(prev => {
          const f = { ...prev, ...updates };
          const sinEntrega = !["street", "colonia", "cp", "city", "state"]
            .some(k => (f[k] || "").trim());
          if (sinEntrega && data.street) {
            f.street  = data.street;  f.colonia = data.colonia;
            f.cp      = data.domicilioFiscalZip;
            f.city    = data.city;    f.state   = data.state;
          }
          return f;
        });
      }

      const criticals = data._warnings.filter(w => w.level === "critical");
      setCsfStatus(data._noText ? "manual" : criticals.length > 0 ? "partial"
                   : data._fuente === "sat" ? "sat-ok" : data._usedOcr ? "ocr-ok" : "success");
    } catch (err) {
      console.error("CSF parse error:", err);
      setCsfStatus("error");
      setCsfResult({ _warnings: [{ field: "file", level: "critical", msg: err.message }], _rawText: "", _confidence: {} });
    }
    if (csfInputRef.current) csfInputRef.current.value = "";
  };

  // Helper: fiscal status badge
  const fiscalBadge = (c) => {
    const hasRfc = !!c.rfc;
    const hasTax = !!c.taxSystem;
    const hasZip = !!c.domicilioFiscalZip;
    if (hasRfc && hasTax && hasZip) return { color: "#4ade80", bg: "#0d1f0d", label: "CFDI ✓" };
    if (hasRfc) return { color: "#facc15", bg: "#1f1a0d", label: "RFC" };
    return { color: "#555", bg: "#111", label: "Sin RFC" };
  };

  if (editing !== null) return (
    <div>
      <SectionTitle>{editing === "new" ? "Nuevo Cliente" : "Editar Cliente"}</SectionTitle>

      {/* CSF Upload */}
      <Card style={{ marginBottom: 16, border: "1px dashed #2a2a2a", background: "linear-gradient(135deg, #0d0d0d, #111)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: "#c9a96e", marginBottom: 4 }}>
              📋 Constancia de Situación Fiscal
            </div>
            <div style={{ fontSize: 12, color: "#888" }}>
              Sube el PDF de la CSF del SAT para llenar automáticamente RFC, razón social, régimen fiscal, domicilio fiscal y CP.
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="file" accept=".pdf" ref={csfInputRef} onChange={handleCSFUpload} style={{ display: "none" }} />
            <button onClick={() => csfInputRef.current?.click()}
              style={{ ...btnPrimary, padding: "10px 20px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              {csfStatus === "parsing" ? "⏳ Leyendo PDF..." :
               typeof csfStatus === "string" && !["", "success", "sat-ok", "ocr-ok", "partial", "manual", "error"].includes(csfStatus) ? "⏳ " + csfStatus :
               "📄 Cargar CSF (PDF)"}
            </button>
            {csfStatus === "sat-ok" && <span style={{ color: "#4ade80", fontSize: 12 }}>✓ Datos confirmados con el SAT</span>}
            {csfStatus === "success" && <span style={{ color: "#4ade80", fontSize: 12 }}>✓ Datos leídos del PDF</span>}
            {csfStatus === "ocr-ok" && <span style={{ color: "#4ade80", fontSize: 12 }}>✓ Datos extraídos por OCR — verifica</span>}
            {csfStatus === "partial" && <span style={{ color: "#f0a050", fontSize: 12 }}>⚠️ Extracción parcial — revisa los datos</span>}
            {csfStatus === "manual" && <span style={{ color: "#f87171", fontSize: 12 }}>❌ No se pudo leer — ingresa manualmente</span>}
            {csfStatus === "error" && <span style={{ color: "#f87171", fontSize: 12 }}>✗ Error al leer el PDF</span>}
            {typeof csfStatus === "string" && !["", "success", "sat-ok", "ocr-ok", "partial", "manual", "error", "parsing"].includes(csfStatus) && (
              <span style={{ color: "#facc15", fontSize: 12 }}>⏳ {csfStatus}</span>
            )}
          </div>
        </div>

        {/* CSF extraction detail */}
        {csfResult && (
          <div style={{ marginTop: 12, padding: 12, background: "#0a0a0a", borderRadius: 6, fontSize: 11 }}>
            <div style={{ color: "#888", letterSpacing: 1, marginBottom: 8, fontSize: 10 }}>RESULTADO DE EXTRACCIÓN</div>
            {[
              { key: "rfc", label: "RFC" },
              { key: "legalName", label: "Razón Social" },
              { key: "taxSystem", label: "Régimen Fiscal", extra: csfResult.taxSystemLabel },
              { key: "domicilioFiscalZip", label: "CP Fiscal" },
              { key: "street", label: "Calle" },
              { key: "colonia", label: "Colonia" },
              { key: "city", label: "Municipio" },
              { key: "state", label: "Estado" },
              { key: "curp", label: "CURP" },
            ].map(f => {
              const value = csfResult[f.key];
              const confidence = csfResult._confidence?.[f.key];
              const warning = csfResult._warnings?.find(w => w.field === f.key);
              const isCritical = warning?.level === "critical";
              const color = !value ? (isCritical ? "#f87171" : "#555") : (warning ? "#f0a050" : "#4ade80");
              return (
                <div key={f.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderBottom: "1px solid #111" }}>
                  <span style={{ color: "#888", width: 100, flexShrink: 0 }}>{f.label}:</span>
                  <span style={{ color, flex: 1, fontFamily: value ? "'DM Sans'" : "inherit", fontWeight: value ? 500 : 400 }}>
                    {value ? (value + (f.extra ? " — " + f.extra : "")) : (isCritical ? "❌ " : "— ") + (warning?.msg || "no extraído")}
                  </span>
                  {confidence && <span style={{ color: "#333", fontSize: 9, marginLeft: 8 }}>{confidence}</span>}
                </div>
              );
            })}
            {/* Lo que dijo la confirmación con el SAT: de dónde salieron los
                datos, si el contribuyente no está activo, si el PDF y el SAT
                no coinciden. Antes solo se enseñaban los avisos de un campo. */}
            {(csfResult._warnings || []).filter(w => w.message).map((w, i) => (
              <div key={i} style={{ marginTop: 6, fontSize: 11,
                color: w.level === "critical" ? "#f87171" : w.level === "warning" ? "#f0a050" : "#888" }}>
                {w.level === "critical" ? "⚠️ " : w.level === "warning" ? "⚠ " : "ℹ "}{w.message}
              </div>
            ))}
            {csfResult._warnings?.length > 0 && csfResult._warnings.some(w => w.level === "critical") && (
              <div style={{ marginTop: 8, padding: 8, background: "#1f0d0d", borderRadius: 4, color: "#f87171", fontSize: 11 }}>
                ⚠️ Algunos campos críticos no se pudieron extraer. Verifica y completa manualmente los campos marcados en rojo antes de timbrar.
              </div>
            )}
            <button onClick={() => {
              const txt = "CSF Raw Text:\n\n" + (csfResult._rawText || "N/A");
              if (navigator.clipboard) { try { navigator.clipboard.writeText(txt); } catch {} }
              alert("Texto del PDF (copiado al portapapeles):\n\n" + (csfResult._rawText || "N/A").substring(0, 2000) + (csfResult._rawText?.length > 2000 ? "\n\n... (truncado)" : ""));
            }} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 10, marginTop: 8, color: "#666" }}>
              🔍 Ver texto raw del PDF
            </button>
          </div>
        )}
      </Card>

      <Card>
        {/* General Info */}
        <div style={{ marginBottom: 20, padding: 16, background: "#0d0d0d", borderRadius: 8, border: "1px solid #1a1a1a" }}>
          <div style={{ fontSize: 11, color: "#c9a96e", letterSpacing: 2, marginBottom: 12 }}>DATOS GENERALES</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label>Nombre / Alias (para búsqueda)</Label>
              <input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Email</Label>
              <input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Regla de precios</Label>
              {puedePrecios ? (
                <select value={form.pricingRule} onChange={e => setForm({ ...form, pricingRule: e.target.value })} style={selectStyle}>
                  {Object.entries(PRICING_RULES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              ) : (
                <SoloLectura>{PRICING_RULES[form.pricingRule]?.label || form.pricingRule}</SoloLectura>
              )}
            </div>
            <div>
              <Label>Descuento %</Label>
              {puedeDescuento ? (
                <NumInput value={form.discount} onChange={v => setForm({ ...form, discount: v })} style={inputStyle} />
              ) : (
                <SoloLectura>{form.discount}%</SoloLectura>
              )}
            </div>
            <div>
              <Label>IVA %</Label>
              <select value={form.ivaRate} onChange={e => setForm({ ...form, ivaRate: parseFloat(e.target.value) })} style={selectStyle}>
                <option value={8}>8% (Frontera)</option>
                <option value={16}>16% (Interior)</option>
              </select>
            </div>
            <div>
              <Label>Forma de pago (SAT)</Label>
              <select value={form.paymentMethod || "03"} onChange={e => setForm({ ...form, paymentMethod: e.target.value })} style={selectStyle}>
                {SAT_FORMA_PAGO.map(f => <option key={f.code} value={f.code}>{f.code} – {f.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Método de pago (SAT)</Label>
              <select value={form.paymentTiming || "PUE"} onChange={e => setForm({ ...form, paymentTiming: e.target.value })} style={selectStyle}>
                {SAT_METODO_PAGO.map(m => <option key={m.code} value={m.code}>{m.code} – {m.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Shipping & Contact Section */}
        <div style={{ marginBottom: 20, padding: 16, background: "#0d0d0d", borderRadius: 8, border: "1px solid #1a1a1a" }}>
          <div style={{ fontSize: 11, color: "#70c9c9", letterSpacing: 2, marginBottom: 12 }}>📦 DIRECCIÓN DE ENVÍO Y CONTACTO</div>
          {/* Pegar dirección — el parser PROPONE, el usuario confirma */}
          <div style={{ marginBottom: 14, padding: 12, background: "#0a0a0a", borderRadius: 6, border: "1px dashed #2a2a2a" }}>
            <Label>📋 Pegar dirección completa</Label>
            <textarea value={pegado} rows={4}
              onChange={e => { setPegado(e.target.value); setPropuesta(e.target.value.trim() ? parsearDireccion(e.target.value, { esperaNombre: !form.name }) : null); }}
              placeholder={"Jorge Bucio\nCerrada de Arcos 1122\nCol. Prados de la Sierra\nSan Pedro Garza García 66230"}
              style={{ ...inputStyle, width: "100%", fontFamily: "inherit", resize: "vertical" }} />
            {propuesta && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                {[["name","Nombre"],["street","Calle"],["colonia","Colonia"],["cp","C.P."],["city","Ciudad"],["state","Estado"],["phone","Teléfono"],["email","Correo"]]
                  .filter(([k]) => propuesta[k])
                  .map(([k,etq]) => (
                    <div key={k} style={{ display: "flex", gap: 8, padding: "2px 0" }}>
                      <span style={{ color: "#666", minWidth: 74 }}>{etq}</span>
                      <span style={{ color: "#e8e8e8" }}>{propuesta[k]}</span>
                      {propuesta.confianza[k] === "cp" && k === "state" &&
                        <span style={{ color: "#8a6d3b", fontSize: 10 }}>deducido del C.P.</span>}
                      {propuesta.confianza[k] === "posicion" &&
                        <span style={{ color: "#8a6d3b", fontSize: 10 }}>por posición — verifica</span>}
                    </div>
                  ))}
                {propuesta.avisos.map((a,i) => (
                  <div key={i} style={{ color: "#c9a96e", marginTop: 6 }}>⚠️ {a}</div>
                ))}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button onClick={() => {
                    const p = propuesta, u = {};
                    if (p.name && !form.name) u.name = p.name;
                    for (const k of ["street","colonia","cp","city","state","phone","email"]) if (p[k]) u[k] = p[k];
                    setForm(prev => ({ ...prev, ...u })); setPegado(""); setPropuesta(null);
                  }} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }}>
                    ✓ Aplicar a dirección de envío
                  </button>
                  <button onClick={() => { setPegado(""); setPropuesta(null); }}
                    style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>Descartar</button>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label>Calle y número</Label>
              <input value={form.street || ""} onChange={e => setForm({ ...form, street: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Colonia</Label>
              <input value={form.colonia || ""} onChange={e => setForm({ ...form, colonia: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Código Postal</Label>
              <input value={form.cp || ""} onChange={e => setForm({ ...form, cp: e.target.value })} style={inputStyle} maxLength={5} />
            </div>
            <div>
              <Label>Ciudad</Label>
              <input value={form.city || ""} onChange={e => setForm({ ...form, city: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Estado</Label>
              <input value={form.state || ""} onChange={e => setForm({ ...form, state: e.target.value })} style={inputStyle} />
            </div>
            <div>
              <Label>Persona de contacto (Atención)</Label>
              <input value={form.att || ""} onChange={e => setForm({ ...form, att: e.target.value })} style={inputStyle} placeholder="Atención a..." />
            </div>
            <div>
              <Label>Teléfono de contacto</Label>
              <input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* Fiscal Data Section */}
        <div style={{ marginBottom: 20, padding: 16, background: "#0d0d0d", borderRadius: 8, border: "1px solid #1a1a1a" }}>
          <div style={{ fontSize: 11, color: "#c9a96e", letterSpacing: 2, marginBottom: 12 }}>🏛 DATOS FISCALES (CFDI 4.0)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Label>Razón Social / Nombre fiscal (exacto como aparece en CSF) *</Label>
              <input value={form.legalName || ""} onChange={e => setForm({ ...form, legalName: e.target.value })}
                style={{ ...inputStyle, borderColor: form.legalName ? "#1a3a1a" : "#2a2a2a" }}
                placeholder="Tal como aparece en la Constancia de Situación Fiscal" />
            </div>
            <div>
              <Label>RFC *</Label>
              <input value={form.rfc || ""} onChange={e => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                style={{ ...inputStyle, fontFamily: "monospace", borderColor: form.rfc ? "#1a3a1a" : "#2a2a2a" }}
                placeholder="XAXX010101000" maxLength={13} />
            </div>
            <div>
              <Label>Régimen Fiscal *</Label>
              {/* Con varios en la CSF se enseña cuántos y cuál se usará por
                  omisión: el desplegable sigue siendo el catálogo completo
                  —un cliente capturado a mano no tiene lista— pero quien
                  subió su CSF ve que hay más de uno y que puede cambiarlo al
                  facturar. */}
              {(form.regimenes || []).length > 1 && (
                <div style={{ fontSize: 11, color: "#c9a96e", marginBottom: 6, lineHeight: 1.6 }}>
                  Su CSF registra {form.regimenes.length} regímenes:{" "}
                  {form.regimenes.map(r => r.code).join(", ")}. El de aquí abajo es
                  el que se usa por omisión; en cada remisión se puede escoger otro.
                </div>
              )}
              <select value={form.taxSystem || ""} onChange={e => setForm({ ...form, taxSystem: e.target.value })}
                style={{ ...selectStyle, borderColor: form.taxSystem ? "#1a3a1a" : "#2a2a2a" }}>
                <option value="">— Seleccionar —</option>
                {SAT_REGIMEN_FISCAL.map(r => <option key={r.code} value={r.code}>{r.code} – {r.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Domicilio Fiscal CP *</Label>
              <input value={form.domicilioFiscalZip || ""} onChange={e => setForm({ ...form, domicilioFiscalZip: e.target.value })}
                style={{ ...inputStyle, fontFamily: "monospace", borderColor: form.domicilioFiscalZip ? "#1a3a1a" : "#2a2a2a" }}
                placeholder="Código postal del domicilio fiscal" maxLength={5} />
            </div>
            <div>
              <Label>Uso de CFDI</Label>
              <select value={form.cfdiUse || "G01"} onChange={e => setForm({ ...form, cfdiUse: e.target.value })} style={selectStyle}>
                {SAT_USO_CFDI.map(u => <option key={u.code} value={u.code}>{u.code} – {u.label}</option>)}
              </select>
            </div>
            {/* Lo que se propone al timbrar; quien timbra lo puede cambiar
                factura por factura. Un distribuidor acredita el IEPS. */}
            <div>
              <Label>IEPS en sus facturas</Label>
              <select value={form.iepsDisplay === "desglosado" ? "desglosado" : "incluido"}
                onChange={e => setForm({ ...form, iepsDisplay: e.target.value })} style={selectStyle}>
                <option value="incluido">Incluido en el precio (consumidor final, restaurante)</option>
                <option value="desglosado">Desglosado (distribuidor: lo acredita)</option>
              </select>
            </div>
            {(form.fiscalStreet || form.fiscalColonia || form.fiscalCity) && (
              <div style={{ gridColumn: "1/-1", fontSize: 12, color: "#666", padding: 8, background: "#111", borderRadius: 6 }}>
                <span style={{ color: "#888" }}>Domicilio fiscal (CSF):</span> {[form.fiscalStreet, form.fiscalColonia, form.fiscalCity, form.fiscalState, form.domicilioFiscalZip].filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        </div>

        {/* Custom Prices Editor
            Para el vendedor es una LISTA, no un editor: ve lo que su cliente
            paga —que necesita para vender— y pide por escrito lo que quiera
            cambiar. Esconderle el precio sería peor que no dejarle moverlo. */}
        <div style={{ marginTop: 16, padding: 16, background: "#0d0d0d", borderRadius: 8, border: "1px solid #1a1a1a" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "#c9a96e", letterSpacing: 2 }}>PRECIOS PERSONALIZADOS</div>
            <div style={{ fontSize: 10, color: "#555" }}>
              {Object.values(form.customPrices || {}).filter(v => v !== "" && v !== undefined).length} productos con precio especial
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 12 }}>
            {puedePrecios
              ? <>Deja en blanco para usar el precio base ({PRICING_RULES[form.pricingRule]?.label}). Los precios personalizados tienen prioridad.</>
              : <>Estos son los precios de este cliente. Para cambiar alguno, pídelo:
                  lo autoriza el dueño o un gerente y se aplica solo.</>}
          </div>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #222", position: "sticky", top: 0, background: "#0d0d0d" }}>
                  <th style={{ textAlign: "left", padding: "6px 4px", color: "#666", fontSize: 10 }}>PRODUCTO</th>
                  <th style={{ textAlign: "right", padding: "6px 4px", color: "#666", fontSize: 10, width: 80 }}>BASE</th>
                  <th style={{ textAlign: "right", padding: "6px 4px", color: "#666", fontSize: 10, width: 100 }}>ESPECIAL</th>
                </tr>
              </thead>
              <tbody>
                {products.filter(p => p.category !== "cortesia" && p.grossPrice > 0).map(p => {
                  const rule = PRICING_RULES[form.pricingRule];
                  const basePrice = (rule?.priceMap && rule.priceMap[p.id] !== undefined) ? rule.priceMap[p.id] : p.grossPrice;
                  const customVal = (form.customPrices || {})[p.id];
                  const hasCustom = customVal !== undefined && customVal !== "";
                  return (
                    <tr key={p.id} style={{ borderBottom: "1px solid #1a1a1a" }}>
                      <td style={{ padding: "6px 4px", color: hasCustom ? "#c9a96e" : "#888", fontSize: 11 }}>{p.name}</td>
                      <td style={{ padding: "6px 4px", textAlign: "right", color: "#555" }}>{fmt(basePrice)}</td>
                      <td style={{ padding: "2px 4px", textAlign: "right" }}>
                        {puedePrecios ? (
                          <input type="number" step="0.01" min="0"
                            value={customVal ?? ""}
                            placeholder={basePrice.toFixed(0)}
                            onChange={e => {
                              const val = e.target.value;
                              setForm(prev => ({
                                ...prev,
                                customPrices: { ...(prev.customPrices || {}), [p.id]: val === "" ? undefined : parseFloat(val) }
                              }));
                            }}
                            style={{ ...inputStyle, width: 90, textAlign: "right", padding: "4px 6px", fontSize: 12,
                              background: hasCustom ? "#1a1a0d" : "#0a0a0a",
                              borderColor: hasCustom ? "#3a3a1a" : "#1a1a1a" }}
                          />
                        ) : (
                          <span style={{ fontSize: 12, color: hasCustom ? "#c9a96e" : "#555" }}>
                            {hasCustom ? fmt(customVal) : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {puedePrecios && Object.values(form.customPrices || {}).some(v => v !== undefined && v !== "") && (
            <button onClick={() => setForm(prev => ({ ...prev, customPrices: {} }))}
              style={{ ...btnSecondary, padding: "4px 12px", fontSize: 11, marginTop: 8, borderColor: "#3a2020", color: "#c97070" }}>
              Limpiar precios personalizados
            </button>
          )}
          {!puedePrecios && puedeSolicitar && editing !== "new" && (
            <button onClick={() => setSolicitando({ ...form, id: editing })}
              style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12, marginTop: 10,
                       borderColor: "#2a3a2a", color: "#a8d060" }}>
              ✋ Solicitar precio especial
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={() => setEditing(null)} style={btnSecondary}>Cancelar</button>
          <button onClick={save} style={btnPrimary}>💾 Guardar</button>
        </div>
      </Card>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <SectionTitle sub={`${clients.length} clientes registrados`}>Clientes</SectionTitle>
        <button onClick={startNew} style={btnPrimary}>+ Nuevo Cliente</button>
      </div>
      <div style={{ marginBottom: 16 }}>
        <input placeholder="Buscar por nombre, ciudad o estado..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 400 }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {filtered.map(c => (
          <Card key={c.id} style={{ padding: "12px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  {[[c.city, c.state].filter(Boolean).join(", "), c.rfc].filter(Boolean).join(" · ")
                    || <span style={{ color: "#8a6d3b" }}>⚠ sin domicilio de entrega</span>}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {(() => { const b = fiscalBadge(c); return (
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: b.bg, color: b.color, border: `1px solid ${b.color}33` }}>
                    {b.label}
                  </span>
                ); })()}
                <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "#1a1a1a", color: "#c9a96e" }}>
                  {PRICING_RULES[c.pricingRule]?.label}
                </span>
                {c.customPrices && Object.values(c.customPrices).filter(v => v !== undefined && v !== "").length > 0 && (
                  <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "#1a1a0d", color: "#c9a96e", border: "1px solid #3a3a1a" }}>
                    ✦ {Object.values(c.customPrices).filter(v => v !== undefined && v !== "").length} precios especiales
                  </span>
                )}
                <span style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, background: "#1a1a1a", color: "#888" }}>
                  IVA {c.ivaRate}% · Desc {c.discount}%
                  {c.aFavor > 0 && (
                    <span style={{ color: "#70c9c9", marginLeft: 8 }}>
                      · a favor {fmt(c.aFavor)}
                    </span>
                  )}
                </span>
                {(() => {
                  // El saldo, a la vista. Antes había que ir a Cuentas para
                  // saber si este cliente debía algo.
                  const r = resumenDe(c.id);
                  if (!r.cuantas) return null;
                  return (
                    <button onClick={() => setHistorialDe(historialDe === c.id ? null : c.id)}
                      title={`${r.cuantas} remisiones · ${fmt(r.total)} vendido`}
                      style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer",
                               background: r.saldo > 0 ? "#160f0f" : "#0d1a0d",
                               border: `1px solid ${r.saldo > 0 ? "#3a1a1a" : "#1a3a1a"}`,
                               color: r.saldo > 0 ? "#e8b0b0" : "#4ade80" }}>
                      🧾 {r.cuantas}
                      {r.saldo > 0 ? ` · debe ${fmt(r.saldo)}` : " · al corriente"}
                    </button>
                  );
                })()}
                <button onClick={() => startEdit(c)} style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12 }}>Editar</button>
                <button onClick={() => setPriceListClient(c)} style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12, borderColor: "#2a2a3a", color: "#a0a0c9" }}>📋 Precios</button>
                <button onClick={() => remove(c.id)} style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12, borderColor: "#4a2020", color: "#c97070" }}>×</button>
              </div>
            </div>

            {historialDe === c.id && (() => {
              const r = resumenDe(c.id);
              return (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #1a1a1a" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                                marginBottom: 8, fontSize: 11, color: "#666" }}>
                    <span>{r.cuantas} remisión{r.cuantas !== 1 ? "es" : ""} · {fmt(r.total)}</span>
                    {r.saldo > 0 && <span style={{ color: "#e8b0b0" }}>saldo {fmt(r.saldo)}</span>}
                  </div>
                  {/* Renglones, no tabla: en un teléfono la celda de acciones
                      quedaba estrechísima y los iconos se apilaban. Mismo
                      arreglo que en Cuentas. */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {r.lista.map(inv => {
                      const cancelada = remisionCancelada(inv);
                      const cob = estadoCobranza(inv);
                      const com = estadoComision(inv);
                      return (
                        <div key={inv.id} style={{ padding: "6px 0", borderBottom: "1px solid #141414",
                                                   opacity: cancelada ? 0.5 : 1, fontSize: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 600, color: "#e8e0d4", minWidth: 52 }}>
                              {esperaFolio(inv) ? "s/folio" : `#${inv.folio}`}
                            </span>
                            <span style={{ color: "#888" }}>{new Date(inv.date).toLocaleDateString("es-MX")}</span>
                            <span style={{ fontSize: 10, color: cancelada ? "#f87171" : cob.color }}>
                              {cancelada ? "Cancelada" : cob.etiqueta}
                            </span>
                            <span style={{ color: "#c9a96e", fontWeight: 600, marginLeft: "auto" }}>{fmt(inv.total)}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                            {/* La comisión de esa venta y en qué estado está.
                                Es a lo que el vendedor viene a esta pantalla. */}
                            {com.clave !== COMISION.sin_comision.clave && (
                              <span title={com.ayuda} style={{ color: com.color, fontSize: 10 }}>
                                {com.etiqueta} · {fmt(inv.comision)}
                              </span>
                            )}
                            <div style={{ marginLeft: "auto" }}>{acciones.barraDe(inv, { compacto: true })}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </Card>
        ))}
      </div>
      {priceListClient && (
        <PriceListPreview
          client={priceListClient}
          products={products}
          vinicola={{ nombre: membresia?.tenant?.name }}
          discount={priceListClient.discount || 0}
          getPrice={(productId) => {
            const c = priceListClient;
            const p = products.find(pp => pp.id === productId);
            if (!p) return 0;
            if (c.customPrices && c.customPrices[productId] !== undefined && c.customPrices[productId] !== "") return Number(c.customPrices[productId]);
            const rule = PRICING_RULES[c.pricingRule];
            if (rule?.priceMap && rule.priceMap[productId] !== undefined) return rule.priceMap[productId];
            return p.grossPrice;
          }}
          onClose={() => setPriceListClient(null)}
        />
      )}
      {solicitando && (
        <SolicitarPrecio
          cliente={solicitando} products={products} membresia={membresia}
          onCerrar={() => setSolicitando(null)} />
      )}
      {acciones.modales}
    </div>
  );
}

/** Un dato que se VE pero no se toca. No es un input apagado: es el valor. */
function SoloLectura({ children }) {
  return (
    <div title="Lo fija la vinícola. Puedes pedir que cambie."
      style={{ padding: "9px 12px", background: "#0d0d0d", border: "1px solid #1a1a1a",
               borderRadius: 6, fontSize: 13, color: "#c9a96e" }}>
      {children}
    </div>
  );
}
